import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import EditVoice from "./EditVoice";
import {
  Box,
  Typography,
  Alert,
  TextField,
  IconButton,
  CircularProgress,
  Modal,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const ExpandedComponent = ({ data }) => {
  const theme = useTheme();
  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>File Name:</strong> {data.filename}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Summary:</strong> {data.summary}
      </Typography>
      <Typography variant="body2">
        <strong>Page Content:</strong> {data.content}
      </Typography>
    </Box>
  );
};

export default function VoiceList({ refresh, chatbotId, permission }) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    document.title = "Upload Voice | KlonIT Web Application";
    fetchDocuments();
  }, [refresh, triggerFetch]);

  useEffect(() => {
    const filtered = documents.filter((doc) =>
      Object.values(doc)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setFilteredDocuments(filtered);
  }, [searchText, documents]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/get-voices`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setDocuments(responseData);
      setFilteredDocuments(responseData);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setError("Failed to fetch voice recordings.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this voice recording?")) {
      return;
    }

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.delete(`/delete-voice/${documentId}`, { params });
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      setFilteredDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      setError("Voice recording has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting voice data:", error);
      setError("Failed to delete voice recording.");
    }
  };

  const handleEdit = (document) => {
    setCurrentDocument(document);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
    setTriggerFetch(!triggerFetch);
  };

  const updateModal = () => {
    fetchDocuments();
    closeModal();
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "File Name",
      selector: (row) => row.filename,
      sortable: true,
      maxWidth: "200px",
    },
    {
      name: "Page Content",
      selector: (row) => row.content,
      sortable: true,
      maxWidth: "200px",
    },
    {
      name: "Summary",
      selector: (row) => row.summary,
      sortable: true,
      maxWidth: "200px",
    },
    {
      name: "Is Scrape?",
      selector: (row) => (row.is_scrape ? "Yes" : "No"),
      sortable: true,
      maxWidth: "125px",
    },
    {
      name: "Is Trained?",
      selector: (row) => (row.is_trained ? "Yes" : "No"),
      sortable: true,
      maxWidth: "125px",
    },
    {
      name: "Upload Date",
      selector: (row) => row.upload_date,
      sortable: true,
      maxWidth: "150px",
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          {permission.canModify && (
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(row)}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.canDelete && (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(row.id)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="text.primary">
            Manage your voice recordings
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search voice..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: "250px" }}
          />
        </Box>

        {error && (
          <Alert
            severity={
              error.includes("deleted") || error.includes("successfully")
                ? "success"
                : "error"
            }
            sx={{ mb: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={30} />
            <Typography mt={2}>Loading voice recordings...</Typography>
          </Box>
        ) : filteredDocuments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <i className="fa fa-microphone fa-3x text-muted" />
            <Typography mt={2} color="text.primary">
              No voice recordings available
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={filteredDocuments}
            pagination
            responsive
            highlightOnHover
            pointerOnHover
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            customStyles={customDataTableStyles}
            theme={theme.palette.mode === "dark" ? "dark" : "default"}
          />
        )}
      </Box>

      {/* Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="edit-voice-modal"
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "900px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            outline: "none",
            overflow: "auto",
          }}
        >
          <EditVoice
            document={currentDocument}
            onClose={closeModal}
            onUpdate={updateModal}
            chatbotId={chatbotId}
            permission={permission}
          />
        </Box>
      </Modal>
    </>
  );
}